import { RemoveRedEyeRounded } from "@mui/icons-material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import { asanaTicketKeys, ticketKeys } from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { PLATFORM_USER_ACTIONS } from "../../../../utils/constants";
import { icons } from "../../environment/icons/iconExport";
import IntegrationImage from "../components/IntegrationImage";

export default function AsanaButton({
  fullControl,
  createAsanaTask,
  linkAsanaTask,
  asanaTaskLink,
  loading,
  sx,
  hideTitle = false,
  disabled = false,
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { trackEvent, deleteAsanaTask, unlinkTicket } = useAPI();
  const [anchorEl, setAnchorEl] = useState(null);
  const [asanaTaskExpanded, setAsanaTaskExpanded] = useState(false);
  const [isRemoveLinkDialogOpen, setIsRemoveLinkDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_TICKET
  );

  // Open dropdown menu
  const handleAsanaButtonClick = (event) => {
    const message = "Dropdown menu for 'Asana Task' button was opened";
    trackEvent(message).then();
    setAnchorEl(event.currentTarget);
    setAsanaTaskExpanded(!asanaTaskExpanded);
  };

  // Close dropdown menu
  const handleCloseMenu = () => {
    const message = "Dropdown menu for 'Asana Task' button was closed";
    trackEvent(message).then();
    setAnchorEl(null);
    setAsanaTaskExpanded(false);
  };

  // View
  const handleViewButton = () => {
    const message =
      "User clicked on 'View' button under 'Asana Task' dropdown menu";
    trackEvent(message).then();

    handleCloseMenu();
    window.open(asanaTaskLink);
  };

  // Create
  const handleCreateAsanaTask = () => {
    const message = `User clicked on 'Create Asana Task' button for control: ${fullControl?.control_id}`;
    trackEvent(message).then();
    handleCloseMenu();
    createAsanaTask();
  };

  // Link
  const handleLinkButton = () => {
    const message =
      "User clicked on 'Link' button under 'Asana Task' dropdown menu";
    trackEvent(message).then();
    linkAsanaTask();
    handleCloseMenu();
  };

  // Remove link
  const handleRemoveLinkButton = () => {
    const message =
      "User clicked on 'Remove Link' button under 'Asana Task' dropdown menu";
    trackEvent(message).then();
    setIsRemoveLinkDialogOpen(true);
  };

  const handleRemoveLinkConfirmButton = async () => {
    const message =
      "User clicked on 'Remove' button for removing Asana task link";
    trackEvent(message).then();
    Toast.info(`Removing Asana task link...`);

    try {
      await unlinkTicket(fullControl.control_id);
      Toast.success(`Asana task link removed successfully!`);
      queryClient.invalidateQueries({ queryKey: asanaTicketKeys.all });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    } catch (error) {
      Toast.error("Removing Asana task link failed, please try again later.");
    }

    handleCloseDialog("removeLink");
  };

  // Delete
  const handleDeleteButton = () => {
    const message =
      "User clicked on 'Delete' button under 'Asana Task' dropdown menu";
    trackEvent(message).then();
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirmButton = () => {
    const message = "User clicked on 'Confirm' button for deleting Asana task";
    trackEvent(message).then();
    Toast.info(`Deleting Asana task...`);

    deleteAsanaTask(fullControl.control_id)
      .then(() => {
        Toast.success(`Asana task deleted successfully!`);
        queryClient.invalidateQueries({ queryKey: asanaTicketKeys.all });
        queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      })
      .catch(() => {
        Toast.error("Deleting Asana task failed, please try again later.");
      });

    handleCloseDialog();
  };

  // Close confirmation dialog
  const handleCloseDialog = (action) => {
    let message = "";
    if (action === "removeLink") {
      setIsRemoveLinkDialogOpen(false);
      message = "Confirmation dialog for remove Asana task link was closed";
    } else {
      setIsDeleteDialogOpen(false);
      message = "Confirmation dialog for delete Asana task was closed";
    }
    trackEvent(message).then();

    handleCloseMenu();
  };

  const actions = {
    View: {
      handler: handleViewButton,
      icon: (
        <RemoveRedEyeRounded
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
    },
    "Create Task": {
      handler: handleCreateAsanaTask,
      icon: (
        <AddCircleOutlineRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: false,
      disabled: !hasPermission,
    },
    "Link Existing": {
      handler: handleLinkButton,
      icon: (
        <AddLinkRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: false,
      disabled: !hasPermission,
    },
    "Remove Link": {
      handler: handleRemoveLinkButton,
      icon: (
        <LinkOffRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
      disabled: !hasPermission,
    },
    "Delete Issue": {
      handler: handleDeleteButton,
      icon: (
        <SvgToIcon
          IconLogo={icons.DeleteBinIcon}
          width={"16px"}
          style={{ color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
      disabled: !hasPermission,
    },
  };

  return (
    <>
      <CustomToolTip title="Asana Task" placement="top">
        <IconButton
          sx={{
            height: "32px",
            width: asanaTaskLink ? "auto" : "32px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            borderRadius: "10px",
            border: "1px solid " + colors.grey_300,
            "&:hover": {
              background: colors.grey_8percent,
              borderColor: colors.gray_main,
            },
            "&.Mui-disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
            },
            ...sx,
          }}
          onClick={handleAsanaButtonClick}
          disabled={loading || disabled}
        >
          {loading ? (
            <CircularProgress size="1rem" sx={{ color: colors.primary_main }} />
          ) : (
            <>
              <IntegrationImage
                width={20}
                name="asana"
                showToolTip={false}
                style={{ minWidth: "20px" }}
              />
              {asanaTaskLink && (
                <InsertLinkRoundedIcon
                  sx={{
                    width: "16px",
                    color: colors.action_active,
                  }}
                />
              )}
              {!hideTitle && (
                <>
                  <Typography
                    variant="overline"
                    sx={{ textTransform: "none", fontWeight: "500" }}
                  >
                    {"Asana Task"}
                  </Typography>
                  {asanaTaskExpanded ? (
                    <ExpandLessRoundedIcon />
                  ) : (
                    <ExpandMoreRoundedIcon />
                  )}
                </>
              )}
            </>
          )}
        </IconButton>
      </CustomToolTip>

      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            width: "146px",
            borderRadius: "9px",
            boxShadow: colors.dropdown_menu_shadow,
            border: "1px solid" + colors.primary_48percent,
            padding: "4px",
          },
        }}
      >
        {Object.keys(actions)
          .filter(
            (actionName) =>
              actions[actionName].showWhenThereIsIssue ===
              (asanaTaskLink !== null)
          )
          .map((actionName) => (
            <CustomToolTipWithPermission
              title={null}
              permission={!actions[actionName].disabled}
            >
              <MenuItem
                onClick={actions[actionName].handler}
                sx={{
                  color: colors.subtitle,
                  padding: 1,
                  gap: 1,
                }}
                disabled={actions[actionName].disabled || disabled}
              >
                {actions[actionName].icon}
                <Typography variant="caption" style={{ textTransform: "none" }}>
                  {actionName}
                </Typography>
              </MenuItem>
            </CustomToolTipWithPermission>
          ))}
      </Menu>

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        openDialog={isDeleteDialogOpen}
        handleDialogClose={() => handleCloseDialog("delete")}
        handleConfirm={handleDeleteConfirmButton}
        dialogTitle="Delete Asana Task"
        dialogContentFirstLine="Are you sure you want to delete the task?"
        confirmationButtonText="Delete"
        destructive={true}
      />

      {/* Remove Link confirmation dialog */}
      <ConfirmationDialog
        openDialog={isRemoveLinkDialogOpen}
        handleDialogClose={() => handleCloseDialog("removeLink")}
        handleConfirm={handleRemoveLinkConfirmButton}
        dialogTitle="Remove Asana Task Link"
        dialogContentFirstLine="Are you sure you want to remove the link?"
        confirmationButtonText="Remove"
        destructive={true}
      />
    </>
  );
}
